"use client";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useId, useMemo, useRef, useState, } from "react";
const FormFieldContext = createContext(null);
export function FormField({ label, hint, error, required = false, name, children }) {
    const id = useId();
    const formState = useContext(FormContext);
    const fromFormError = name && formState ? formState.errors[name] : undefined;
    const resolvedError = error ?? fromFormError;
    const hintId = `${id}-hint`;
    const errorId = `${id}-error`;
    const describedBy = [hint ? hintId : null, resolvedError ? errorId : null].filter(Boolean).join(" ") || undefined;
    const ctx = { id, describedBy, invalid: Boolean(resolvedError), required };
    return (_jsxs("div", { className: `form-field ${resolvedError ? "has-error" : ""}`, children: [_jsx("label", { htmlFor: id, className: "form-field-label", children: _jsxs("span", { children: [label, required ? _jsx("span", { "aria-hidden": "true", className: "required-mark", children: " *" }) : null] }) }), _jsx(FormFieldContext.Provider, { value: ctx, children: children }), hint ? _jsx("small", { id: hintId, children: hint }) : null, resolvedError ? (_jsx("strong", { id: errorId, role: "alert", children: resolvedError })) : null] }));
}
/**
 * Read the surrounding `FormField`'s id/aria-describedby/aria-invalid/
 * aria-required so a custom input can wire the same a11y plumbing as the
 * built-in primitives. Returns an empty object when used outside a
 * `FormField` so consumers can render bare.
 */
export function useFieldAttrs() {
    const ctx = useContext(FormFieldContext);
    if (!ctx)
        return {};
    return {
        id: ctx.id,
        "aria-describedby": ctx.describedBy,
        "aria-invalid": ctx.invalid || undefined,
        "aria-required": ctx.required || undefined,
        required: ctx.required || undefined,
    };
}
export function TextInput({ value, defaultValue, onChange, onValueChange, placeholder, type = "text", disabled, readOnly, autoComplete, inputMode, name, maxLength, minLength, pattern, }) {
    const attrs = useFieldAttrs();
    const isControlled = onChange !== undefined || onValueChange !== undefined;
    const handleChange = (event) => {
        onChange?.(event);
        onValueChange?.(event.currentTarget.value);
    };
    const valueProps = isControlled
        ? { value: value ?? "", onChange: handleChange }
        : { defaultValue: defaultValue ?? value };
    return (_jsx("input", { className: "text-input", type: type, placeholder: placeholder, disabled: disabled, readOnly: readOnly, autoComplete: autoComplete, inputMode: inputMode, name: name, maxLength: maxLength, minLength: minLength, pattern: pattern, ...valueProps, ...attrs }));
}
export function TextareaField({ ref, value, defaultValue, onChange, onValueChange, placeholder, disabled, readOnly, name, rows = 4, maxLength, minLength, }) {
    const attrs = useFieldAttrs();
    const isControlled = onChange !== undefined || onValueChange !== undefined;
    const handleChange = (event) => {
        onChange?.(event);
        onValueChange?.(event.currentTarget.value);
    };
    const valueProps = isControlled
        ? { value: value ?? "", onChange: handleChange }
        : { defaultValue: defaultValue ?? value };
    return (_jsx("textarea", { ref: ref, className: "textarea-input", placeholder: placeholder, disabled: disabled, readOnly: readOnly, name: name, rows: rows, maxLength: maxLength, minLength: minLength, ...valueProps, ...attrs }));
}
export function SelectField({ value, defaultValue, onChange, onValueChange, options, disabled, name, }) {
    const attrs = useFieldAttrs();
    const isControlled = onChange !== undefined || onValueChange !== undefined;
    const handleChange = (event) => {
        onChange?.(event);
        onValueChange?.(event.currentTarget.value);
    };
    const valueProps = isControlled
        ? { value: value ?? "", onChange: handleChange }
        : { defaultValue: defaultValue ?? value };
    return (_jsx("select", { className: "select-input", disabled: disabled, name: name, ...valueProps, ...attrs, children: options.map((option) => (_jsx("option", { value: option, children: option }, option))) }));
}
export function CheckboxField({ label, checked, defaultChecked, onChange, onCheckedChange, disabled, name, value, }) {
    const isControlled = onChange !== undefined || onCheckedChange !== undefined;
    const handleChange = (event) => {
        onChange?.(event);
        onCheckedChange?.(event.currentTarget.checked);
    };
    const checkedProps = isControlled
        ? { checked: checked ?? false, onChange: handleChange }
        : { defaultChecked: defaultChecked ?? checked ?? false };
    return (_jsxs("label", { className: "choice-field", children: [_jsx("input", { type: "checkbox", disabled: disabled, name: name, value: value, ...checkedProps }), _jsx("span", { children: label })] }));
}
export function ToggleField({ label, checked, defaultChecked, onChange, onCheckedChange, disabled, name, }) {
    const isControlled = onChange !== undefined || onCheckedChange !== undefined;
    const handleChange = (event) => {
        onChange?.(event);
        onCheckedChange?.(event.currentTarget.checked);
    };
    const checkedProps = isControlled
        ? { checked: checked ?? false, onChange: handleChange }
        : { defaultChecked: defaultChecked ?? checked ?? false };
    return (_jsxs("label", { className: "toggle-field", children: [_jsx("span", { children: label }), _jsx("input", { type: "checkbox", role: "switch", disabled: disabled, name: name, ...checkedProps }), _jsx("i", { "aria-hidden": "true" })] }));
}
function isValidNumericDraft(draft, allowDecimals, allowNegative) {
    if (draft === "" || draft === "-")
        return allowNegative || draft === "";
    // Permit one optional sign, optional integer part, optional single decimal point + fractional part.
    const pattern = allowDecimals
        ? /^-?(\d+)?(\.\d*)?$/
        : /^-?\d*$/;
    if (!pattern.test(draft))
        return false;
    if (!allowNegative && draft.startsWith("-"))
        return false;
    return true;
}
function formatNumeric(value, precision) {
    if (precision === undefined)
        return String(value);
    return value.toFixed(Math.max(0, precision));
}
function clampNumeric(value, min, max) {
    let next = value;
    if (typeof min === "number" && next < min)
        next = min;
    if (typeof max === "number" && next > max)
        next = max;
    return next;
}
function roundToPrecision(value, precision) {
    if (precision === undefined)
        return value;
    const factor = 10 ** Math.max(0, precision);
    return Math.round(value * factor) / factor;
}
/**
 * Numeric input with controlled+uncontrolled dual mode, clamping on blur,
 * keyboard step (ArrowUp/Down ±step, Shift× ten), and Increment/Decrement
 * buttons. Sanitizes keystrokes against a numeric grammar so the visible
 * draft can stay mid-edit (e.g. "-", "1.", "0.") without tripping on
 * `parseFloat` round-trips. Wires to FormField (id, aria-describedby,
 * aria-invalid, aria-required) and to Form's native FormData submission
 * via the standard `name` attribute.
 *
 * The committed value is `null` when the input is empty (and not
 * required) — distinct from `0`. Validation should treat `null` as
 * "not provided".
 */
export function NumberInput({ value, defaultValue, onValueChange, onChange, min, max, step = 1, precision, prefix, suffix, stepperPosition = "trailing", placeholder, disabled, readOnly, name, autoComplete, inputMode, }) {
    const attrs = useFieldAttrs();
    const isControlled = value !== undefined;
    const allowDecimals = precision === undefined || precision > 0;
    const allowNegative = min === undefined || min < 0;
    const resolvedInputMode = inputMode ?? (allowDecimals ? "decimal" : "numeric");
    const initialDraft = (() => {
        const seed = isControlled ? value : (defaultValue ?? value);
        if (seed === null || seed === undefined)
            return "";
        return formatNumeric(seed, precision);
    })();
    const [draft, setDraft] = useState(initialDraft);
    const inputRef = useRef(null);
    // When controlled, mirror external value into the draft unless the user
    // is mid-edit and the parsed draft already matches. Pattern: derive state
    // during render via a stored prevValue, per
    // https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
    const [prevControlledValue, setPrevControlledValue] = useState(value);
    if (isControlled && value !== prevControlledValue) {
        setPrevControlledValue(value);
        const currentParsed = draft === "" || draft === "-" ? null : Number(draft);
        const matchesCurrent = draft !== "" && currentParsed === value;
        if (!matchesCurrent) {
            setDraft(value === null || value === undefined ? "" : formatNumeric(value, precision));
        }
    }
    const commit = useCallback((rawDraft) => {
        const trimmed = rawDraft.trim();
        if (trimmed === "" || trimmed === "-") {
            return { draft: "", numeric: null };
        }
        const parsed = Number(trimmed);
        if (Number.isNaN(parsed))
            return { draft: "", numeric: null };
        const clamped = clampNumeric(parsed, min, max);
        const rounded = roundToPrecision(clamped, precision);
        return { draft: formatNumeric(rounded, precision), numeric: rounded };
    }, [min, max, precision]);
    const applyStep = useCallback((delta) => {
        const current = draft === "" || draft === "-" ? 0 : Number(draft);
        const tentative = (Number.isFinite(current) ? current : 0) + delta;
        const clamped = clampNumeric(tentative, min, max);
        const rounded = roundToPrecision(clamped, precision);
        const next = formatNumeric(rounded, precision);
        setDraft(next);
        onValueChange?.(rounded);
    }, [draft, min, max, precision, onValueChange]);
    const handleInputChange = (event) => {
        const next = event.currentTarget.value;
        if (!isValidNumericDraft(next, allowDecimals, allowNegative)) {
            // Reject the keystroke by snapping back to the previous draft.
            event.preventDefault();
            return;
        }
        setDraft(next);
        onChange?.(event);
        // Emit a numeric callback only when the draft parses cleanly to a
        // finite number, so consumers don't see noisy mid-edit values.
        if (next === "" || next === "-") {
            onValueChange?.(null);
        }
        else {
            const parsed = Number(next);
            if (Number.isFinite(parsed))
                onValueChange?.(parsed);
        }
    };
    const handleBlur = () => {
        const { draft: nextDraft, numeric } = commit(draft);
        setDraft(nextDraft);
        onValueChange?.(numeric);
    };
    const handleKeyDown = (event) => {
        if (event.key === "ArrowUp") {
            event.preventDefault();
            applyStep(event.shiftKey ? step * 10 : step);
        }
        else if (event.key === "ArrowDown") {
            event.preventDefault();
            applyStep(event.shiftKey ? -step * 10 : -step);
        }
        else if (event.key === "Home" && typeof min === "number") {
            event.preventDefault();
            const rounded = roundToPrecision(min, precision);
            setDraft(formatNumeric(rounded, precision));
            onValueChange?.(rounded);
        }
        else if (event.key === "End" && typeof max === "number") {
            event.preventDefault();
            const rounded = roundToPrecision(max, precision);
            setDraft(formatNumeric(rounded, precision));
            onValueChange?.(rounded);
        }
    };
    const showSteppers = stepperPosition !== "none";
    const steppers = showSteppers ? (_jsxs("span", { className: "number-input-steppers", "aria-hidden": "true", children: [_jsx("button", { type: "button", tabIndex: -1, className: "number-input-stepper number-input-stepper--up", onClick: () => {
                    inputRef.current?.focus();
                    applyStep(step);
                }, disabled: disabled || readOnly, "aria-label": "Increment", children: "+" }), _jsx("button", { type: "button", tabIndex: -1, className: "number-input-stepper number-input-stepper--down", onClick: () => {
                    inputRef.current?.focus();
                    applyStep(-step);
                }, disabled: disabled || readOnly, "aria-label": "Decrement", children: "\u2212" })] })) : null;
    return (_jsxs("span", { className: `number-input number-input--steppers-${stepperPosition}`, children: [stepperPosition === "leading" ? steppers : null, prefix ? _jsx("span", { className: "number-input-affix number-input-affix--prefix", children: prefix }) : null, _jsx("input", { ref: inputRef, className: "number-input-input text-input", type: "text", inputMode: resolvedInputMode, autoComplete: autoComplete ?? "off", placeholder: placeholder, disabled: disabled, readOnly: readOnly, name: name, value: draft, onChange: handleInputChange, onBlur: handleBlur, onKeyDown: handleKeyDown, ...attrs }), suffix ? _jsx("span", { className: "number-input-affix number-input-affix--suffix", children: suffix }) : null, stepperPosition === "trailing" ? steppers : null] }));
}
const FormContext = createContext(null);
/**
 * Read the surrounding `<Form>`'s state. Returns `null` when used outside
 * a form (so consumers can degrade gracefully).
 */
export function useFormState() {
    return useContext(FormContext);
}
/**
 * Lightweight `<Form>` wrapper that orchestrates submit + validation
 * without requiring controlled state on every field. Reads field values
 * from the native `FormData` on submit, runs the optional validator,
 * exposes errors via context to nested `FormField`s and `FormSummary`.
 *
 * Pairs with the existing field primitives (TextInput, SelectField,
 * CheckboxField, ToggleField) which already accept a `name` prop —
 * just give each field a `name` and the wrapper does the rest.
 *
 * For schema-first validation (zod / yup / ajv), pass an adapter via
 * the `validate` prop — see the `FormValidator` jsdoc for an example.
 */
export function Form({ children, onSubmit, validate, onValidationError, resetOnSubmit = false, noValidate = true, className, id, "aria-label": ariaLabel, }) {
    const generatedId = useId();
    const formId = id ?? generatedId;
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const setFieldError = useCallback((field, error) => {
        setErrors((current) => {
            const next = { ...current };
            if (error === undefined)
                delete next[field];
            else
                next[field] = error;
            return next;
        });
    }, []);
    const handleSubmit = async (event) => {
        event.preventDefault();
        const formData = new FormData(event.currentTarget);
        const values = Object.fromEntries(formData.entries());
        if (validate) {
            const validationErrors = validate(values) ?? {};
            const hasErrors = Object.keys(validationErrors).length > 0;
            setErrors(validationErrors);
            if (hasErrors) {
                onValidationError?.(validationErrors);
                return;
            }
        }
        else {
            setErrors({});
        }
        setSubmitting(true);
        try {
            await onSubmit(values, event);
            if (resetOnSubmit)
                event.currentTarget.reset();
        }
        finally {
            setSubmitting(false);
        }
    };
    const ctx = useMemo(() => ({ id: formId, errors, submitting, setFieldError }), [formId, errors, submitting, setFieldError]);
    return (_jsx(FormContext.Provider, { value: ctx, children: _jsx("form", { id: formId, className: className, "aria-label": ariaLabel, noValidate: noValidate, onSubmit: handleSubmit, children: children }) }));
}
/**
 * Renders a roll-up of all form-level errors as an `aria-live="assertive"`
 * region. Consumers drop it inside `<Form>` (typically near the submit
 * button) and it auto-announces when validation fails.
 *
 * Returns null when there are no errors, so it's safe to render
 * unconditionally.
 */
export function FormSummary({ title = "Please fix the following:", className }) {
    const ctx = useFormState();
    const entries = ctx ? Object.entries(ctx.errors) : [];
    if (entries.length === 0)
        return null;
    return (_jsxs("div", { className: ["form-summary", className].filter(Boolean).join(" "), role: "alert", "aria-live": "assertive", children: [_jsx("strong", { children: title }), _jsx("ul", { children: entries.map(([field, message]) => (_jsxs("li", { children: [_jsx("span", { className: "form-summary-field", children: field }), _jsx("span", { className: "form-summary-message", children: message })] }, field))) })] }));
}
